/**
 * useGroupedCreatives - Groups creative_metadata rows by group_id
 * for display in the Load Creatives modal.
 *
 * Each "ModalRow" = one creative group (or one ungrouped creative).
 */

import { useMemo } from "react";
import { useLoadCreatives, type LoadCreative } from "./useLoadCreatives";

// ===== Types =====

export interface VariantInfo {
  creative: LoadCreative;
  slot: "feed" | "vertical" | "horizontal";
}

export interface ModalRow {
  /** group_id for grouped creatives, or creative.id for ungrouped */
  id: string;
  /** Display name (group name with dimension suffixes stripped) */
  displayName: string;
  /** "image" | "video" */
  type: "image" | "video";
  /** Coverage label */
  coverage: "Triple" | "Pair" | "Single";
  /** Thumbnail URL (prefer square > vertical > horizontal) */
  thumbnailUrl: string | null;
  /** Which variants exist in this group */
  variants: {
    feed: LoadCreative | null;
    vertical: LoadCreative | null;
    horizontal: LoadCreative | null;
  };
  /** All member creatives */
  members: LoadCreative[];
  /** Whether this is a grouped entry */
  isGrouped: boolean;
}

// ===== Helpers =====

const DIMENSION_SUFFIX_RE = /[_\- ]?\d{3,4}x\d{3,4}$/i;

function stripDimensionSuffix(name: string): string {
  // Remove file extension first, then dimension suffix, don't re-add extension
  const withoutExt = name.replace(/\.[^/.]+$/, "");
  return withoutExt.replace(DIMENSION_SUFFIX_RE, "").trim() || withoutExt;
}

function classifySlot(creative: LoadCreative): "feed" | "vertical" | "horizontal" {
  // Use aspect_ratio_category from DB if available
  if (creative.aspect_ratio === "vertical") return "vertical";
  if (creative.aspect_ratio === "horizontal") return "horizontal";
  if (creative.aspect_ratio === "feed" || creative.aspect_ratio === "square") return "feed";

  // Fallback to dimension-based classification
  if (!creative.dimensions) return "feed";
  const parts = creative.dimensions.split("x").map(Number);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return "feed";
  const ratio = parts[0] / parts[1];

  if (ratio >= 0.52 && ratio <= 0.60) return "vertical";
  if (ratio >= 1.73 && ratio <= 1.83) return "horizontal";
  return "feed";
}

// ===== Grouping =====

export function groupCreatives(allCreatives: LoadCreative[]): ModalRow[] {
  const groups = new Map<string, LoadCreative[]>();
  const ungrouped: LoadCreative[] = [];

  for (const c of allCreatives) {
    if (c.group_id) {
      const list = groups.get(c.group_id) || [];
      list.push(c);
      groups.set(c.group_id, list);
    } else {
      ungrouped.push(c);
    }
  }

  const rows: ModalRow[] = [];

  // Process groups
  for (const [groupId, members] of groups) {
    let feed: LoadCreative | null = null;
    let vertical: LoadCreative | null = null;
    let horizontal: LoadCreative | null = null;

    for (const m of members) {
      const slot = classifySlot(m);
      if (slot === "feed" && !feed) feed = m;
      else if (slot === "vertical" && !vertical) vertical = m;
      else if (slot === "horizontal" && !horizontal) horizontal = m;
    }

    const variantCount = [feed, vertical, horizontal].filter(Boolean).length;
    const primaryCreative = feed || vertical || horizontal;
    if (!primaryCreative) continue;

    rows.push({
      id: groupId,
      displayName: stripDimensionSuffix(primaryCreative.filename),
      type: primaryCreative.creative_type === "video" ? "video" : "image",
      coverage: variantCount >= 3 ? "Triple" : variantCount === 2 ? "Pair" : "Single",
      thumbnailUrl: feed?.thumbnail_url || vertical?.thumbnail_url || horizontal?.thumbnail_url || null,
      variants: { feed, vertical, horizontal },
      members,
      isGrouped: true,
    });
  }

  // Process ungrouped
  for (const c of ungrouped) {
    const slot = classifySlot(c);
    rows.push({
      id: c.id,
      displayName: stripDimensionSuffix(c.filename),
      type: c.creative_type === "video" ? "video" : "image",
      coverage: "Single",
      thumbnailUrl: c.thumbnail_url,
      variants: {
        feed: slot === "feed" ? c : null,
        vertical: slot === "vertical" ? c : null,
        horizontal: slot === "horizontal" ? c : null,
      },
      members: [c],
      isGrouped: false,
    });
  }

  // Sort by newest first (use latest created_at among members)
  rows.sort((a, b) => {
    const aDate = Math.max(...a.members.map(m => m.created_at ? new Date(m.created_at).getTime() : 0));
    const bDate = Math.max(...b.members.map(m => m.created_at ? new Date(m.created_at).getTime() : 0));
    return bDate - aDate;
  });

  return rows;
}

// ===== Hook =====

export function useGroupedCreatives(
  adAccountId: string | undefined,
  userId: string | undefined
) {
  const { data: creatives, isLoading } = useLoadCreatives(adAccountId, userId);
  const allCreatives = creatives || [];

  const modalRows = useMemo(() => groupCreatives(allCreatives), [allCreatives]);

  return { modalRows, allCreatives, isLoading };
}
